import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";

interface SimOperationModalProps {
  open: boolean;
  onClose: () => void;
  autoSdcId: number;
  simId: string;
  currentOperation: string;
  selectedOperation: "testing" | "waiting_running" | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface ModalContent {
  title: string;
  content: string;
  nextOperation: string;
}

const SimOperationModal: React.FC<SimOperationModalProps> = ({
  open,
  onClose,
  autoSdcId,
  simId,
  currentOperation,
  selectedOperation,
  onSuccess,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const getModalContent = (): ModalContent => {
    switch (currentOperation) {
      case "waiting_operation":
        if (selectedOperation === "testing") {
          return {
            title: "SIMを試験用に有効にします。",
            content:
              "翌日8:00には無効になりますが、通信があると有効になります。試験が終了したら速やかに終了させてください。よろしいですか？",
            nextOperation: "testing",
          };
        } else if (selectedOperation === "waiting_running") {
          return {
            title: "SIMを運用状態にします。",
            content:
              "SIMが[運用待ち]になり、SDCが接続すると[運用中]になります。SDCがoffの場合は、電源がonになると接続します。SDCがonの場合はしばらくすると接続します。頻繁に運用を停止すると別途切り替え料金が発生しますので、できるだけそのままお使いください。よろしいですか？",
            nextOperation: "waiting_running",
          };
        }
        // フォールバックケース
        return {
          title: "SIM操作",
          content: "操作を選択してください。",
          nextOperation: "waiting_operation",
        };
      case "testing":
        return {
          title: "SIMの試験運用を停止します。",
          content:
            "再開するには、[運用]または[試験]のボタンをクリックします。よろしいですか？",
          nextOperation: "waiting_operation",
        };
      case "waiting_running":
        return {
          title: "SIMの運用待ちを解除します。",
          content: "よろしいですか？",
          nextOperation: "waiting_operation",
        };
      case "in_running":
        return {
          title: "SIMを今すぐ無効にします。",
          content:
            "無効にすると[試験]または[運用]の操作ができるようになります。ただし[試験][運用]の操作には別料金が発生します。よろしいですか？",
          nextOperation: "waiting_operation",
        };
      default:
        return {
          title: "SIM操作",
          content: "不明な操作です。",
          nextOperation: "waiting_operation",
        };
    }
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const jwtToken = await getJwtTokenFromIdToken();
      const { nextOperation } = getModalContent();
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/sdcs/${autoSdcId}/sim`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({
            simId,
            nextSimOperation: nextOperation,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.result) {
          onSuccess("SIM操作が成功しました。");
          onClose();
        } else {
          onError("SIM操作に失敗しました。");
        }
      } else {
        onError("SIM操作に失敗しました。");
      }
    } catch (error) {
      console.error("Error:", error);
      onError("エラーが発生しました。");
    } finally {
      setIsLoading(false);
    }
  };

  const { title, content } = getModalContent();

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} /> : "OK"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimOperationModal;
